<template>
  <div
    :id="id"
    :class="className"
    :style="{height: height, width: width, display: 'inline-block'}"
  />
</template>

<script lang="ts">
import { defineComponent, onActivated, onBeforeUnmount, onDeactivated, onMounted, nextTick, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import Decimal from 'decimal.js'
import { init, EChartsOption } from 'echarts'
import resize from '@/components/charts/mixins/resize'
import { AccountBalanceModel } from '@/model/paymentSlip/paymentSlipModel'
import { formatDate, formatThousands } from '@/utils'
export default defineComponent({
  name: 'PaymentSlipCharts',
  props: {
    className: {
      type: String,
      default: 'chart'
    },
    id: {
      type: String,
      default: 'lineChartsCompoents'
    },
    chartInfo: {
      type: Object,
      default: {} as AccountBalanceModel
    },
    chartData: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    company: {
      type: Array as PropType<any[]>,
      default: () => []
    },
    width: {
      type: String,
      default: '500px',
      required: true
    },
    height: {
      type: String,
      default: '270px',
      required: true
    }
  },
  setup(props) {
    const { t } = useI18n()
    const {
      mounted,
      chart,
      beforeDestroy,
      activated,
      deactivated
    } = resize()
    const initChart = () => {
      const title = t('company.companyCode') + '：' + props.company.find(c => c.id === props.chartInfo.companyId).companyCode + '    ' + t('pay.payingBank') + '：' + props.chartInfo.bankName
      const totalAmt = props.chartInfo.amt
      const availableAmt = props.chartInfo.availableAmt
      const yValue = props.chartData.map(c => c.yValue)
      const xValue = props.chartData.map(c => c.xValue)
      if (xValue.length < 1) {
        xValue.push(formatDate(new Date(), 'MM-dd'))
        yValue.push(0)
      }
      const totalBar: string[] = []
      let maxY = totalAmt.toString()
      for (let i = 0; i < yValue.length; i++) {
        if (i === 0) {
          totalBar.push('0')
        } else {
          totalBar.push(new Decimal(totalBar[i - 1]).plus(new Decimal(yValue[i - 1])).toString())
        }
        const max = new Decimal(totalBar[i]).plus(new Decimal(yValue[i]))
        if (new Decimal(maxY).lt(max)) {
          maxY = max.toString()
        }
      }
      maxY = new Decimal(maxY).mul(new Decimal('1.2')).toString()
      const barChart = init(document.getElementById(props.id) as HTMLDivElement)
      barChart.setOption({
        // backgroundColor: '#394056',
        title: {
          top: 5,
          text: title,
          textStyle: {
            fontWeight: 'normal',
            fontSize: 15
            // color: '#F1F1F3'
          },
          left: '1%'
        },
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: ['amt'],
          show: false
        },
        grid: {
          top: 60,
          left: '2%',
          right: '2%',
          bottom: '2%',
          containLabel: true
        },
        xAxis: {
          name: t('pay.payDate'),
          type: 'category',
          data: xValue
        },
        yAxis: {
          name: t('pay.paymentAmount'),
          type: 'value',
          max: maxY
        },
        series: [{
          name: 'Placeholder',
          type: 'bar',
          stack: 'Total',
          itemStyle: {
            borderColor: 'transparent',
            color: 'transparent'
          },
          emphasis: {
            itemStyle: {
              borderColor: 'transparent',
              color: 'transparent'
            }
          },
          data: totalBar
        }, {
          name: 'amt',
          type: 'bar',
          stack: 'Total',
          label: {
            show: true,
            position: 'top',
            formatter: (params: any) => {
              return formatThousands(params.value)
            }
          },
          markLine: {
            data: [{
              name: t('pay.currentAccountBalance') + ': ' + formatThousands(totalAmt),
              yAxis: totalAmt,
              lineStyle: {
                color: 'green'
              },
              label: {
                formatter: '{b}',
                position: 'insideStartTop'
              }
            }, {
              name: t('pay.currentAvailableBalance') + ': ' + formatThousands(availableAmt),
              yAxis: availableAmt,
              lineStyle: {
                color: 'red'
              },
              label: {
                formatter: '{b}',
                position: 'insideEndTop'
              }
            }],
            label: {
              distance: [20, 8]
            }
          },
          data: yValue
        }]
      } as EChartsOption)
      chart.value = barChart
    }
    onMounted(() => {
      mounted()
      nextTick(() => {
        initChart()
      })
    })

    onBeforeUnmount(() => {
      beforeDestroy()
    })

    onActivated(() => {
      activated()
    })

    onDeactivated(() => {
      deactivated()
    })

    return {

    }
  }
})
</script>
